import type { ToolDefinition, ToolType } from "../types";

const OPTIMIZE_WEB_IMAGE_TOOL_TYPE = {
  label: "Web Performance Tool",
  icon: "Globe",
} as const satisfies ToolType;

const optimizeWebImageTool: ToolDefinition = {
  slug: "optimize-web-image",
  name: "Web Image Optimizer: Compress & Resize for PageSpeed",
  title: "Optimize Images for Web",
  description:
    "Free online photo size reducer to optimize images for website performance. Fix 'Serve images in next-gen formats' in Google PageSpeed instantly.",
  longDescription:
    "Boost your website speed and improve Core Web Vitals with our snappy image optimization tool. The web profile shows its defaults before processing: quality 75, WebP output, and a max side of 1920px. Leave fields blank to use those defaults, or override target KB, quality, width, height, sharpen, and enhance for a specific production asset.",
  toolType: {
    label: "Web Optimizer",
    icon: "Globe",
  },
  heroBadges: [
    "100% Free",
    "No Account Required",
    "Boosts PageSpeed",
    "Default WebP Profile",
    "High-Quality WebP",
  ],
  category: "Optimization Tools",
  categorySlug: "image-optimization-tools",
  href: "/tools/optimize-web-image",
  api: "/api/v1/optimize/web",
  keywords: [
    "snappy image optimization tool",
    "free online photo size reducer",
    "convert png to webp high quality",
    "analyze pagespeed image warnings",
    "compress jpeg for website faster",
    "shrink picture without losing quality",
    "serve graphics in next gen formats",
    "properly size website visuals app",
    "decrease file dimensions for web",
    "lighthouse format conversion checker",
    "best way to optimize assets",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Optimize an Image for the Web",
        description:
          "Follow these simple steps to reduce file size and format your visuals for maximum website performance in seconds.",
      },
      benefits: {
        title: "Why Use Our Free Online Photo Size Reducer?",
        description:
          "Discover how optimizing your media assets improves user experience, lowers bounce rates, and boosts your organic search rankings.",
      },
      faqs: {
        title: "Frequently Asked Questions About Web Optimization",
        description:
          "Learn more about next-gen image formats, PageSpeed Insights requirements, and best practices for serving fast websites.",
      },
    },
    intro: {
      title: "The Right Format and Size for the Web",
      paragraphs: [
        "In today's fast-paced digital landscape, serving heavy, unoptimized photos can drastically hurt your website's performance and user experience. Visitors expect pages to load in milliseconds, and search engines like Google heavily penalize slow websites. If you want to know the best way to optimize assets for a flawless user experience, it starts with an intelligent snappy image optimization tool designed specifically for modern web standards.",
        "Our free online photo size reducer takes the guesswork out of technical SEO. Whether you need to decrease file dimensions for web layouts or compress jpeg for website faster loading times, this tool automates the entire process. By automatically scaling excessively large uploads to a maximum web-friendly width of 1920px and converting them with high-efficiency encoding, you can shrink picture without losing quality and immediately enhance your site's speed metrics.",
      ],
      note: "Properly optimized visuals are essential for passing Google's Core Web Vitals assessment and improving your Search rankings.",
    },
    introBlocks: [
      {
        kind: "text",
        title: "How to Fix 'Serve Images in Next-Gen Formats'",
        level: "h2",
        paragraphs: [
          "One of the most frequent errors developers see when they analyze pagespeed image warnings is the suggestion to 'serve images in next-gen formats.' Traditional formats like JPEG and PNG are universally supported, but they lack the superior compression capabilities of modern formats like WebP. Google Lighthouse directly flags these older formats because they consume unnecessary bandwidth and slow down page rendering.",
          "Our lighthouse format conversion checker and optimizer is the perfect automated solution. With a single click, you can convert png to webp high quality, retaining the visual fidelity of your original graphic while slashing the file size by up to 70%. When you serve graphics in next gen formats, mobile devices render your pages significantly faster, leading to lower bounce rates and an improved user experience.",
        ],
        note: "WebP is natively supported by all modern browsers, making it the safest and most efficient format for web delivery.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "Core Benefits for Website Performance",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                label: "Faster Load Times",
                icon: "Zap",
                description:
                  "Reduce image payloads to ensure your pages render instantly on both desktop and mobile networks.",
                accent: "primary",
              },
              {
                label: "Higher SEO Rankings",
                icon: "TrendingUp",
                description:
                  "Meet Google's strict Core Web Vitals criteria to boost your organic search visibility.",
                accent: "primary",
              },
              {
                label: "Bandwidth Savings",
                icon: "HardDrive",
                description:
                  "Lower your server and CDN costs by delivering lightweight WebP files instead of heavy legacy formats.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "Image Format Comparison for Web",
            description:
              "Understanding the difference between legacy formats and next-gen web standards.",
            columns: [
              "Format",
              "Best Use Case",
              "Compression Efficiency",
              "Browser Support",
            ],
            rows: [
              {
                Format: "WebP (Next-Gen)",
                "Best Use Case":
                  "Universal web delivery, photography, graphics",
                "Compression Efficiency":
                  "Excellent (Up to 30% smaller than JPEG)",
                "Browser Support":
                  "Universally supported across modern browsers",
              },
              {
                Format: "JPEG (Legacy)",
                "Best Use Case": "Offline storage, legacy email clients",
                "Compression Efficiency": "Moderate (Lossy)",
                "Browser Support": "Universal",
              },
              {
                Format: "PNG (Legacy)",
                "Best Use Case": "Logos requiring transparency on old systems",
                "Compression Efficiency": "Poor (Lossless but heavy)",
                "Browser Support": "Universal",
              },
            ],
            note: "Our optimizer automatically upgrades legacy files to the highly efficient WebP format.",
            emphasisColumns: ["Format", "Compression Efficiency"],
            monospaceColumns: [],
          },
        ],
      },
      {
        kind: "text",
        title: "Solving the 'Properly Size Images' Warning",
        level: "h2",
        paragraphs: [
          "Another massive drain on website performance is serving visuals that are much larger than the user's screen. If you upload a massive 4K photograph to be displayed in a small 600px blog column, the browser still has to download the entire heavy 4K file before shrinking it visually. This triggers the 'properly size images' penalty in PageSpeed Insights.",
          "Using our properly size website visuals app solves this automatically. Our web preset intelligently scales down excessively large images to a maximum of 1920px—the ideal upper limit for full-width desktop hero banners. This guarantees you will decrease file dimensions for web deployment safely, ensuring crisp quality on large monitors without the wasted bandwidth that drags down your performance scores.",
        ],
        note: "Always pair optimized dimensions with the HTML width and height attributes to prevent Cumulative Layout Shift (CLS).",
      },
    ],
    callouts: [
      {
        title: "Related Performance Tools",
        layout: "cards",
        iconColor: "accent",
        items: [
          {
            label: "SEO Image Optimizer",
            href: "/tools/optimize-seo-responsive-image",
            description:
              "Build responsive WebP `srcset` packages when a single optimized image is not enough.",
            accent: "accent",
          },
        ],
      },
    ],
    benefits: [
      {
        icon: "ImageDown",
        title: "Drastically Reduce File Sizes",
        description:
          "Shrink picture without losing quality by utilizing advanced compression algorithms tailored specifically for the modern web.",
      },
      {
        icon: "Activity",
        title: "Boost Core Web Vitals",
        description:
          "Directly improve your LCP (Largest Contentful Paint) score by serving lightweight, optimized graphics to your visitors.",
      },
      {
        icon: "Settings",
        title: "Shown Defaults, Optional Overrides",
        description:
          "Use the default quality 75 WebP profile, or fine-tune target KB, quality, dimensions, sharpen, and enhance when an image has a stricter requirement.",
      },
      {
        icon: "MonitorSmartphone",
        title: "Mobile & Desktop Ready",
        description:
          "Ensures your hero banners and blog photos look crisp on retina screens while loading blazingly fast on 4G and 5G connections.",
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload your image",
        description:
          "Drag and drop your image onto the upload zone or click to browse. Works seamlessly with JPG, PNG, WebP, and all standard web formats.",
      },
      {
        number: "02",
        title: "Review defaults or add overrides",
        description:
          "Leave the fields blank to use the default quality 75 WebP output with a 1920px max side, or enter target KB, quality, width, height, sharpen, and enhance overrides.",
      },
      {
        number: "03",
        title: "Click Optimize for Web",
        description:
          "Run the web profile. Blank settings use the shown defaults, while any values you entered are sent as query overrides.",
      },
      {
        number: "04",
        title: "Download your optimized file",
        description:
          "Click 'Download Optimized Image' to save the processed WebP file locally to your device once the conversion is complete.",
      },
      {
        number: "05",
        title: "Deploy to your website",
        description:
          "Upload the lightweight, next-gen file to your CMS, WordPress installation, or site build to instantly improve your loading speeds.",
      },
    ],
    faqs: [
      {
        question: "What is the best image format for SEO performance in 2026?",
        answer:
          "WebP is currently the gold standard for web performance and SEO. It provides significantly better compression than JPEG or PNG while maintaining high visual fidelity, making it the best way to optimize assets for modern browsers.",
      },
      {
        question:
          "How do I fix the 'Serve images in next-gen formats' warning?",
        answer:
          "To clear this warning in Google PageSpeed Insights, you must replace older formats like JPEG and PNG with modern equivalents like WebP. Our free online photo size reducer automatically converts your uploads into highly compressed WebP files to satisfy this requirement.",
      },
      {
        question: "Does resizing images to 1920px improve PageSpeed scores?",
        answer:
          "Yes. Serving 4K or 5K images forces browsers to download massive amounts of unnecessary data. By capping the maximum width at 1920px, you provide enough resolution for a full-HD desktop monitor while drastically cutting file weight and improving your LCP score.",
      },
      {
        question:
          "How to reduce image file size without losing visual quality for web?",
        answer:
          "The secret is using a smart encoder that balances compression with perceived quality. Our snappy image optimization tool uses advanced algorithms to discard invisible data, allowing you to shrink picture without losing quality noticeable to the human eye.",
      },
      {
        question:
          "How do I optimize hero images for Core Web Vitals LCP score?",
        answer:
          "Your hero image is often the Largest Contentful Paint (LCP) element. To optimize it, you must compress jpeg for website faster loading, convert it to a next-gen format, properly size the dimensions, and ensure your HTML uses 'fetchpriority=\"high\"'. Our tool handles the compression, conversion, and sizing steps.",
      },
      {
        question:
          "What is the fastest way to optimize images for a production website?",
        answer:
          "For individual files, leave the settings blank and run the default web profile: quality 75 WebP with a 1920px max side. If a CMS, ad slot, or hero section needs an exact output, use the optional target KB, quality, width, height, sharpen, and enhance controls before optimizing.",
      },
      {
        question: "Is WebP or AVIF better for website performance this year?",
        answer:
          "While AVIF offers slightly better compression in some extreme cases, WebP remains the most practical and universally supported format across all modern browsers and content management systems. Our tool defaults to WebP to ensure maximum compatibility.",
      },
    ],
  },
};

export default optimizeWebImageTool;
export { optimizeWebImageTool };
